<template lang="pug">
#waiter-progress-history-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 経過記録の修正

    .box-body.register-body(v-if="history")
      FormDatetimePicker(v-model="datetime" :date.sync="datetimeDate" :time.sync="datetimeTime"
        key-name="日時" name="datetime" placeholder="日時" :validation-rules="{ required: true }" required)
      FormInput(
        v-model="history.title"
        key-name="タイトル" name="title" type="text" placeholder="タイトル"
        :validation-rules="{ required: true, max: 100 }" :maxlength="100"
        :styles="{ maxWidth: '720px', width: '720px' }" required)
      FormTextarea(
        v-model="history.text"
        key-name="本文" name="text" placeholder="本文"
        :validation-rules="{ required: true, max: 1000 }" :maxlength="1000"
        :styles="{ maxWidth: '720px', width: '720px', minHeight: '160px' }" required)

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmModal" type="button") 更新
        button.btn.cancel-btn(@click="gotoWaiterDetail" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="経過記録を更新しますか？" @ok="updateHistory")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoWaiterDetail")
  
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";

import { userStore } from "@/store/store";
import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";

import WaiterProgressHistoriesService, { WaiterProgressHistoriesGetModel, WaiterProgressHistoriesPatchModel } from "@/commons/services/WaiterProgressHistories";

import PageLoading from "@/pc/basics/PageLoading.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormDatetimePicker from "@/pc/components/forms/FormDatetimePicker.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";

/**
 * ### 待機者登録画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    FormDatetimePicker,
    FormInput,
    FormTextarea
  },
  $_veeValidate: { validator: "new" }
})
export default class WaiterProgressHistoryEdit extends Vue {
  /** 待機者経過履歴サービス */
  @Inject("WaiterProgressHistoriesService") waiterProgressHistoriesService!: WaiterProgressHistoriesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 経過記録 */
  history: WaiterProgressHistoriesGetModel | null = null;

  /** 開始日時 */
  datetime: number | null = null;
  /** 開始日時(日) */
  datetimeDate: string = "";
  /** 開始日時(時) */
  datetimeTime: string = "";

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const residentIdInSystem: number = Number(this.$route.params.idinsystem);
    const historyid: number = Number(this.$route.params.historyid);
    const history: WaiterProgressHistoriesGetModel = await this.getWaiterProgressHistory(residentIdInSystem, historyid);
    this.history = history;

    this.datetime = history.datetime;
    this.datetimeDate = this.$dateUtil.convertNumberToString(history.datetime, "yyyy-mm-dd");
    this.datetimeTime = this.$dateUtil.convertNumberToString(history.datetime, "hh:MM:00");

    this.isBusy = false;

    return;
  }

  /**
   * ### 待機者経過履歴を取得する
   */
  public async getWaiterProgressHistory(residentIdInSystem: number, historyid: number): Promise<WaiterProgressHistoriesGetModel> {
    const record: WaiterProgressHistoriesGetModel = await this.waiterProgressHistoriesService.get(userStore, residentIdInSystem, historyid);
    return record;
  }

  public async showConfirmModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();

    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 経過記録の更新
   */
  public async updateHistory(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const history: WaiterProgressHistoriesGetModel = this.history as WaiterProgressHistoriesGetModel;
      const requestBody: WaiterProgressHistoriesPatchModel = await this.createRequestBody(history);
      await this.waiterProgressHistoriesService.patch(userStore, history.residentIdInSystem, history.id, requestBody);

      // 更新成功ダイアログを表示する
      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### リクエストボディを作成する
   */
  public async createRequestBody(history: WaiterProgressHistoriesGetModel): Promise<WaiterProgressHistoriesPatchModel> {
    const requestBody: WaiterProgressHistoriesPatchModel = {
      facilityId: history.facilityId,
      datetime: this.datetime as number,
      title: history.title,
      text: history.text as string,
      updated: history.updated
    };

    return requestBody;
  }

  /**
   * ### 待機者詳細に遷移する
   */
  public gotoWaiterDetail(): void {
    this.$router.push({
      name: PcUrl.WaiterDetail,
      params: { facilityid: this.$route.params.facilityid, idinsystem: this.$route.params.idinsystem, residentid: this.$route.params.residentid }
    });
    return;
  }
}
</script>

<style lang="scss" scoped>
#waiter-progress-history-edit {
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
